import { flatten, includes, isEmpty, get } from 'lodash';
import { matchPath } from 'react-router';
import { getConfig } from '../../config/portal';
import { getCurrentRoutes as rts } from '../language';
import { generateSearchPath } from './boats';
import { generateSearchPath as generateEngineSearchPath } from './engines';
import { geti18nLocations } from '../../utils/language';
import { getPartyBrandedSRP } from './party';
import { getBoatUrl } from './boat';
import { getEngineUrl } from './engine';

const REDIRECT_FACET_NAMES = ['type', 'class', 'make', 'model', 'id', 'dealer', 'bdp', 'edp', 'year', 'fuel', 'category', 'country','city','region','subdivision'];

export const getMappedValue = (oldValue, facetMap = {}) => {
  const { valuesMap = {}, replacers = [], exceptions = [] } = facetMap;
  if (valuesMap[oldValue] || valuesMap[oldValue] === '') {
    return valuesMap[oldValue];
  }

  let newValue = oldValue;
  replacers.forEach(({ search, replacer }) => {
    if (!includes(exceptions, newValue)) {
      newValue = newValue.replace(new RegExp(search.join('|'), 'g'), replacer);
    }
  });
  return newValue.toLowerCase();
};

const validateQueryString = ({ query }, queryString) => {
  if (!query) {
    return true;
  }

  const invalidQueryString = Object.entries(query)
    .some(([queryParam, queryValue]) => !(new RegExp(`(\\?|&)${queryParam}=${queryValue}(&|$)`)).test(queryString));

  return !invalidQueryString;
};

const getMappedSearchParams = (searchParams, mappedValues) => {
  const mappedSearchParams = {};
  if (!mappedValues) {
    return mappedSearchParams;
  }
  if (searchParams.includes('multiFacetedBoatTypeClass')) {
    if (mappedValues.type) {
      mappedSearchParams.multiFacetedBoatTypeClass = { [mappedValues.type]: [] };
      if (mappedValues.class) {
        mappedSearchParams.multiFacetedBoatTypeClass[mappedValues.type] = [mappedValues.class];
      }
    }
  }
  if (searchParams.includes('makeModel')) {
    if (mappedValues.make) {
      mappedSearchParams.makeModel = { [mappedValues.make]: [] };
      if (mappedValues.model) {
        mappedSearchParams.makeModel[mappedValues.make] = [mappedValues.model];
      }
    }
  }
  if (searchParams.includes('condition')) {
    if (mappedValues.condition) {
      mappedSearchParams.condition = mappedValues.condition;
    }
  }
  // Location
  if (searchParams.includes('country')) {
    if (mappedValues.country) {
      mappedSearchParams.country = mappedValues.country;
    } else if (mappedValues.region) { //This logic is only used while we activate subdivisions on engines
      // eslint-disable-next-line no-unused-vars
      const [country, _] = mappedValues.region.split('.');
      mappedSearchParams.country = country;
    } else if (mappedValues.subdivision) {
      // eslint-disable-next-line no-unused-vars
      const [_, __, country] = mappedValues.subdivision.split('@');
      mappedSearchParams.country = country;
    } else if (mappedValues.city && mappedValues.city.includes('@')) {
      // eslint-disable-next-line no-unused-vars
      const [_, __, ___, country] = mappedValues.city.split('@');
      mappedSearchParams.country = country;
    }
  }
  if (searchParams.includes('region')) {
    if (mappedValues.region) {
      const [country, region] = mappedValues.region.split('.');
      mappedSearchParams.region = region;
      mappedSearchParams.country = mappedSearchParams.country || country;
    }
  }
  if (searchParams.includes('subdivision')) {
    if (mappedValues.subdivision) {
      const [subdivision, region, country] = mappedValues.subdivision.split('@');
      mappedSearchParams.subdivision = subdivision;
      mappedSearchParams.region = mappedSearchParams.region || region;
      mappedSearchParams.country = mappedSearchParams.country || country;
    }
  }
  if (searchParams.includes('city')) {
    if (mappedValues.city) {
      if (mappedValues.city.includes('@')) {
        const [city, subdivision, region, country] = mappedValues.city.split('@');
        mappedSearchParams.city = [city];
        mappedSearchParams.subdivision = mappedSearchParams.subdivision || subdivision;
        mappedSearchParams.region = mappedSearchParams.region || region;
        mappedSearchParams.country = mappedSearchParams.country || country;
      } else {
        mappedSearchParams.city = [mappedValues.city];
      }
    }
  }

  if (searchParams.includes('hullMaterial')) {
    if (mappedValues.hullMaterial) {
      mappedSearchParams.hullMaterial = mappedValues.hullMaterial;
    }
  }
  if (searchParams.includes('fuel')) {
    if (mappedValues.fuel) {
      mappedSearchParams.fuel = mappedValues.fuel;
    }
  }
  // For engines
  if (searchParams.includes('category')) {
    if (mappedValues.category) {
      mappedSearchParams.category = mappedValues.category;
    }
  }
  if (searchParams.includes('type')) {
    mappedSearchParams.type = get(mappedValues, 'type');
  }
  return mappedSearchParams;
};

const redirectToLanguage = (url, language) => {
  const localizedLocation = geti18nLocations(url);
  for (const location of localizedLocation) {
    if (location.lang === language) {
      return location.route;
    }
  }
  return url;
};

export const sortWildcardFacetsPriority = (wildcards) => {
  const sortedWildcardFacets = Object.keys(wildcards || {});
  return sortedWildcardFacets.sort((a, b) => {
    if (wildcards[a].priority === wildcards[b].priority) {
      return 0;
    } else if (wildcards[a].priority === 'high') {
      return -1;
    }
    return 1;
  });
};

export const getMappedValues = (params, facetsMap = {}) => {
  const mappedValues = {};
  const { wildcards } = facetsMap;
  const facetsUsed = [];
  const sortedWildcardFacets = sortWildcardFacetsPriority(wildcards);

  REDIRECT_FACET_NAMES.forEach((facetName) => {
    if (params[facetName]) {
      mappedValues[facetName] = getMappedValue(params[facetName], facetsMap[facetName]);
    }
  });

  if (mappedValues.class && !mappedValues.type) {
    mappedValues.type = getMappedValue(params.class, facetsMap.type);
  }

  for (const wildcard in params) {
    const wildcardParam = params[wildcard];
    let paramFound = false;
    if (!wildcardParam) { break; }
    if (!wildcard.includes('wildcard')) { continue; }

    for (let i = 0; i < sortedWildcardFacets.length; i++) {
      if (facetsUsed.includes(sortedWildcardFacets[i])) { continue; }
      const valuesMap = wildcards[sortedWildcardFacets[i]].valuesMap;
      if (valuesMap[wildcardParam]) {
        mappedValues[sortedWildcardFacets[i]] = getMappedValue(params[wildcard], wildcards[sortedWildcardFacets[i]]);
        facetsUsed.push(sortedWildcardFacets[i]);
        paramFound = true;
        break;
      }
    }
    if (!paramFound) {
      mappedValues.model = getMappedValue(params[wildcard], get(wildcards, 'model'));
    }
  }
  return mappedValues;
};

/*
 * Compares two urls using 2 criteria:
 * 1. length of params: longer urls, based on number of "/", preceed shorter ones
 * 2. alphabetically: in ascending order
 */
export const urlComparator = (urlA, urlB) => {
  if (urlA.split('/').length === urlB.split('/').length) {
    return urlA.localeCompare(urlB);
  } else if (urlA.split('/').length > urlB.split('/').length) {
    return -1;
  }
  return 1;
};

/**
 * Compares two URLs based on the length of the origin URL, alphabetically.
 * Use the first element of the origin array.
 *
 * @returns location redirects map based on the origin URL lenght
 */
export const getLocationRedirects = () => {
  const locationRedirects = get(getConfig(), 'redirects.locationRedirects', []);
  return locationRedirects.sort((elementA, elementB) => {
    return urlComparator(elementA.origin[0], elementB.origin[0]);
  });
};

export const getRedirectsUrlPattern = () => {
  const redirectsMaps = get(getConfig(), 'redirects.redirectsMaps', []);
  return flatten(redirectsMaps.map(({ origins }) => origins.map((urlPattern) => {
    return `${urlPattern.path || urlPattern}`;
  }),
  )).sort(urlComparator);
};

export const mapRedirectResultsToValues = (mappedValues, results) => {
  if (results.year) {
    mappedValues.year = results.year;
  }
  if (results.make) {
    mappedValues.make = results.make;
  }
  if (results.model) {
    mappedValues.model = results.model;
  }
};

export const getDestinationPath = (redirectsMaps, path, search, mappedValues, id) => {
  const redirectMap = redirectsMaps.find((redirectMap) => matchPath(path, { path: redirectMap.origins, exact: true }));
  if (!redirectMap) {
    return;
  }

  const isQueryValid = validateQueryString(redirectMap, search);
  if (!isQueryValid) {
    return;
  }

  if (redirectMap.route) {
    return { destPathname: rts()[redirectMap.route] };
  }
  if (redirectMap.pathRewrite && redirectMap.pathRewrite.length === 2) {
    return { destPathname: path.replace(redirectMap.pathRewrite[0], redirectMap.pathRewrite[1]) + (redirectMap.appendToPath ? redirectMap.appendToPath : '') };
  }
  if (redirectMap.article) {
    return { destPathname: redirectMap.article };
  }
  if (redirectMap.search) {
    mappedValues && Object.keys(mappedValues).forEach(key => {
      mappedValues[key] = typeof mappedValues[key] === 'string' && mappedValues[key].replace('-html', '');
    });
    const newSearch = Object.assign({}, (isEmpty(redirectMap.search)) ? mappedValues : redirectMap.search);
    let destPathname;
    if (newSearch.type === 'engines') {
      destPathname = generateEngineSearchPath({}, newSearch, true);
    } else {
      destPathname = generateSearchPath({}, newSearch, true);
    }
    if (redirectMap.language) {
      destPathname = redirectToLanguage(destPathname, redirectMap.language);
    }
    return { destPathname };
  }
  if (redirectMap.dealer) {
    const normalizedName = mappedValues.dealer;
    const party = { id, normalizedName };
    let destPathname = getPartyBrandedSRP(party);
    return { destPathname };
  }
  if (redirectMap.bdp) {
    mappedValues.id = id;
    let destPathname = getBoatUrl(mappedValues);
    return { destPathname };
  }
  if (redirectMap.edp) {
    mappedValues.id = id;
    let destPathname = getEngineUrl(mappedValues);
    return { destPathname };
  }
  if (redirectMap.errorCode) {
    return { errorCode: redirectMap.errorCode };
  }
  if (redirectMap.searchParams) {
    const searchParams = getMappedSearchParams(redirectMap.searchParams, mappedValues);
    let destPathname;
    if (searchParams.type === 'engines') {
      destPathname = generateEngineSearchPath({}, searchParams, true);
    } else {
      destPathname = generateSearchPath({}, searchParams, true);
    }
    return { destPathname };
  }
};
